// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
/**
 * race.ts — the netcode, which is deliberately almost nothing.
 *
 * Boxbox multiplayer is a PARALLEL SAME-SEED RACE. Every peer runs its own `Game`
 * over its own calls, from a seed the host froze into the round start. No peer
 * ever touches another peer's car, so there is no shared mutable state and
 * structurally nothing to desync. What crosses the wire is a status ping (which
 * stop you are on, your running time) and a final result — display data, both.
 *
 * Ranking is by LOWEST total time, so the leader is whoever has spent the fewest
 * seconds. Results are BROADCAST, not unicast to the host, so a promoted peer
 * already holds every result it needs to publish the ladder — which is the whole
 * of host transfer here (takeover.test.ts).
 *
 * The one thing that can genuinely go wrong is a room that waits forever on
 * somebody who closed their tab, so the ladder publishes on a visible countdown
 * from the first finisher (principle #12), and a peer that leaves is dropped from
 * the expected set. Timers are setInterval, never rAF: a backgrounded tab freezes
 * rAF, and a countdown that stops when you switch tabs is a room that hangs.
 */

import type { Summary } from './game';

export interface RaceNet {
  readonly selfId: string;
  channel<T>(
    name: string,
    onReceive: (data: T, from: string) => void,
  ): ((data: T, toPeers?: string | string[]) => void) & { off: () => void };
}

/** How long the room holds the ladder open for stragglers after the first finish. */
export const LADDER_GRACE_MS = 25_000;
const TICK_MS = 250;

export interface RaceSeat {
  id: string;
  name: string;
}

/** The compact result that crosses the wire and drives the ladder. */
export interface RaceResult {
  total: number;
  optimal: number;
  lost: number;
  stops: number;
  catastrophes: number;
  /** Seconds lost at each stop (stop time + penalties), for a real breakdown. */
  perStop: number[];
  /** Jobs serviced at each stop. */
  fixed: number[];
}

export function resultOf(summary: Summary): RaceResult {
  return {
    total: summary.total,
    optimal: summary.optimal,
    lost: summary.lost,
    stops: summary.stops,
    catastrophes: summary.catastrophes,
    perStop: summary.rows.map((r) => r.stopTime + r.penalty),
    fixed: summary.rows.map((r) => r.fixed.length),
  };
}

export interface Standing {
  id: string;
  name: string;
  isSelf: boolean;
  /** 0-based stop they are on (or completed). */
  stop: number;
  stops: number;
  /** Running total in seconds. */
  total: number;
  done: boolean;
  result: RaceResult | null;
  gone: boolean;
}

interface StatusMsg {
  r: number;
  stop: number;
  stops: number;
  total: number;
}

interface FinMsg extends RaceResult {
  r: number;
}

interface LadderMsg {
  r: number;
  order: string[];
}

export interface RaceConfig {
  net?: RaceNet | null;
  round: number;
  seats: RaceSeat[];
  selfId: string;
  isHost: boolean;
  graceMs?: number;
  onChange?: () => void;
  onLadder?: () => void;
}

export interface Race {
  standings(): Standing[];
  ping(stop: number, stops: number, total: number): void;
  finish(result: RaceResult): void;
  settled(): boolean;
  closesInMs(): number | null;
  setHost(isHost: boolean): void;
  isHost(): boolean;
  peerLeft(id: string): void;
  closeNow(): void;
  destroy(): void;
}

export function createRace(cfg: RaceConfig): Race {
  const net = cfg.net ?? null;
  const round = cfg.round;
  const graceMs = cfg.graceMs ?? LADDER_GRACE_MS;

  let host = cfg.isHost;
  let published = false;
  let destroyed = false;
  let deadline: number | null = null;
  let elapsed = 0;
  let timer: ReturnType<typeof setInterval> | undefined;
  let order: string[] | null = null;

  const seats = new Map<string, Standing>();
  for (const s of cfg.seats) {
    seats.set(s.id, {
      id: s.id,
      name: s.name,
      isSelf: s.id === cfg.selfId,
      stop: 0,
      stops: 0,
      total: 0,
      done: false,
      result: null,
      gone: false,
    });
  }

  const changed = (): void => cfg.onChange?.();

  const sendSt = net?.channel<StatusMsg>('st', (msg, from) => {
    if (destroyed || msg.r !== round) return;
    const s = seats.get(from);
    if (!s || s.done) return;
    s.stop = msg.stop;
    s.stops = msg.stops;
    s.total = msg.total;
    changed();
  });

  const sendFin = net?.channel<FinMsg>('fin', (msg, from) => {
    if (destroyed || msg.r !== round) return;
    record(from, msg);
  });

  const sendLad = net?.channel<LadderMsg>('lad', (msg) => {
    if (destroyed || msg.r !== round || published) return;
    order = msg.order;
    publishLocally();
  });

  function record(id: string, msg: FinMsg): void {
    const s = seats.get(id);
    if (!s || s.result) return;
    s.done = true;
    s.total = msg.total;
    s.stop = msg.stops;
    s.stops = msg.stops;
    s.result = {
      total: msg.total,
      optimal: msg.optimal,
      lost: msg.lost,
      stops: msg.stops,
      catastrophes: msg.catastrophes,
      perStop: msg.perStop ?? [],
      fixed: msg.fixed ?? [],
    };
    changed();
    openWindow();
    considerClosing();
  }

  function expected(): Standing[] {
    return [...seats.values()].filter((s) => !s.gone);
  }

  function allIn(): boolean {
    const exp = expected();
    return exp.length > 0 && exp.every((s) => s.result !== null);
  }

  function openWindow(): void {
    if (published || deadline !== null) return;
    if (![...seats.values()].some((s) => s.result)) return;
    deadline = graceMs;
    elapsed = 0;
    ensureTimer();
    changed();
  }

  function ensureTimer(): void {
    if (timer || destroyed || published) return;
    timer = setInterval(tick, TICK_MS);
  }

  function stopTimer(): void {
    if (timer) clearInterval(timer);
    timer = undefined;
  }

  function tick(): void {
    if (destroyed || published || deadline === null) return;
    elapsed += TICK_MS;
    if (elapsed >= deadline) {
      if (host) closeNow();
      else changed();
      return;
    }
    changed();
  }

  function considerClosing(): void {
    if (published || !host) return;
    if (allIn()) closeNow();
  }

  function rank(): string[] {
    // Finished cars first, by LOWEST total; a tie goes to the fewer catastrophes,
    // then a stable id so every peer sorts identically.
    return [...seats.values()]
      .sort((a, b) => {
        const ar = a.result;
        const br = b.result;
        if (!!ar !== !!br) return ar ? -1 : 1;
        if (ar && br) {
          if (ar.total !== br.total) return ar.total - br.total;
          if (ar.catastrophes !== br.catastrophes) return ar.catastrophes - br.catastrophes;
        }
        return a.id < b.id ? -1 : 1;
      })
      .map((s) => s.id);
  }

  function publishLocally(): void {
    if (published) return;
    published = true;
    stopTimer();
    deadline = null;
    changed();
    cfg.onLadder?.();
  }

  function closeNow(): void {
    if (published || destroyed) return;
    order = rank();
    sendLad?.({ r: round, order });
    publishLocally();
  }

  return {
    standings(): Standing[] {
      const list = [...seats.values()];
      if (order) {
        const at = new Map(order.map((id, i) => [id, i]));
        list.sort((a, b) => (at.get(a.id) ?? 99) - (at.get(b.id) ?? 99));
      } else {
        // Live: whoever has done the most stops leads, ties broken by less time.
        list.sort((a, b) => b.stop - a.stop || a.total - b.total || (a.id < b.id ? -1 : 1));
      }
      return list;
    },

    ping(stop, stops, total): void {
      if (destroyed) return;
      const me = seats.get(cfg.selfId);
      if (me && !me.done) {
        me.stop = stop;
        me.stops = stops;
        me.total = total;
        changed();
      }
      sendSt?.({ r: round, stop, stops, total });
    },

    finish(result): void {
      if (destroyed) return;
      const msg: FinMsg = { r: round, ...result };
      record(cfg.selfId, msg);
      sendFin?.(msg);
      if (!net) publishLocally();
    },

    settled: () => published,
    closesInMs: () => (deadline === null || published ? null : Math.max(0, deadline - elapsed)),

    setHost(isHost: boolean): void {
      host = isHost;
      if (!host) return;
      if (!published) {
        if (allIn()) return closeNow();
        if (deadline !== null) ensureTimer();
        else openWindow();
      }
      changed();
    },

    isHost: () => host,

    peerLeft(id: string): void {
      const s = seats.get(id);
      if (!s || s.gone) return;
      s.gone = true;
      changed();
      considerClosing();
    },

    closeNow,

    destroy(): void {
      destroyed = true;
      stopTimer();
      sendSt?.off();
      sendFin?.off();
      sendLad?.off();
    },
  };
}
